<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_BUSINESS_AUTOMATION')"
            label="Add"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="nterBranchAccountAccountRef"
      :busy="isBusy"
      :items="interBranchAccountList"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_BUSINESS_AUTOMATION')"
              @click="onSelectInterBranchAccount(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Modify</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectInterBranchAccount(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onDeleteInterBranchTransaction(data.item, $event.target)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <!-- Pagination -->
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      :ok-disabled="isLoading"
      size="md"
      :title="modalTitle"
      @hidden="resetModal"
      @ok="handleSubmit"
    >
      <validation-observer ref="nterBranchAccountRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Account Types -->
            <b-col md="12">
              <SelectField
                id="account-type"
                v-model="interBranchAccountFormData.accountType"
                label="Account Type"
                :options="accountTypeOptions"
                :reduce="option => option.value"
                error-name="Account Type"
                rules="required"
                item="label"
                placeholder="Select"
                :set-selected="getAccounts"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account Types -->

            <!-- Account -->
            <b-col md="12">
              <SelectField
                id="account"
                v-model="interBranchAccountFormData.accountNumber"
                label="Account"
                :options="accountNumberOptions"
                :reduce="option => option.value"
                error-name="Account"
                rules="required"
                item="label"
                placeholder="Select"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Account -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      no-close-on-backdrop
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="md"
      title="Filters"
      modal-footer
      @ok="fetchInterBranchAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Types -->
        <b-col md="12">
          <SelectField
            id="branch"
            v-model="filter.branchID"
            label="Account Types"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            item="label"
            placeholder="Select"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account Types -->

        <!-- Account -->
        <b-col md="12">
          <SelectField
            id="account"
            v-model="filter.accountNumber"
            label="Account"
            :options="accountNumberOptions"
            :reduce="option => option.value"
            item="label"
            placeholder="Select"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Account -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BModal, BSpinner,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    TablePagination,
    TableEntries,
    Button,
    BTable,
    BSpinner,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    SelectField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isBusy: true,
      buttonLabel: 'Save',
      modalTitle: 'Create Inter-Branch Account',
      isReadonly: false,
      required,
      isRequired: false,
      isLoading: false,
      sortDesc: false,
      showSpinner: false,
      toAmount,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true },
        { key: 'accountNumber', sortable: true },
        { key: 'branchName', sortable: true },
        { key: 'actions' },
      ],
      accountTypeOptions: [
        { label: 'Inter-Branch Transaction Settlement Account (System)', value: '20419000' },
        { label: 'Inter-Branch Suspense Account (Manual)', value: '10640000' },
      ],
      interBranchAccountFormData: {
        accountType: '',
        accountNumber: '',
      },
      cashVolumeOptions: [
        { label: 'Unrequired', value: 1 },
        { label: 'Required', value: 2 },
      ],
      interBranchAccountList: [],
      accountNumberOptions: [],
      filter: {
        branchID: '',
        accountNumber: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
  },
  mounted() {
    // this.getAccounts()
    this.fetchInterBranchAccounts()
  },
  methods: {
    ...mapActions({
      getInterBranchAccountList: 'operations/getInterBranchAccountList',
      createInterBranchAccount: 'operations/createInterBranchAccountAction',
      updateInterBranchAccount: 'operations/updateInterBranchAccountAction',
      getAccountCategoryByCode: 'account/getAccountCategoryByCode',
      deleteInterBranchTransaction: 'operations/deleteBusinessAutomation',
    }),

    onSelectInterBranchAccount(items, activity, val) {
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? 'Edit Inter-Branch  Account' : 'Inter-Branch Account'
      this.interBranchAccountFormData = { ...items }
      this.buttonLabel = 'Update'
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async interBranchAccountAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === 'Save'
          ? await this.createInterBranchAccount({
            ...this.interBranchAccountFormData,
            accountType: this.interBranchAccountFormData.accountType === '20419000' ? 'InterBranchTransactionSettlementAccount' : 'InterBranchSuspenseAccount',
          })
          : await this.updateInterBranchAccount({
            ...this.interBranchAccountFormData,
            accountType: this.interBranchAccountFormData.accountType === '20419000' ? 'InterBranchTransactionSettlementAccount' : 'InterBranchSuspenseAccount',
          })
        const { status } = response
        if (status === 200) {
          this.fetchInterBranchAccounts()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: 'Inter-Branch Account created successfully' })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.nterBranchAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.interBranchAccountAction, { text: `Are you sure you want to ${this.buttonLabel} this Inter-branch transaction account Account?` })
        }
      })
    },

    async fetchInterBranchAccounts() {
      try {
        this.isBusy = true
        const response = await this.getInterBranchAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.interBranchAccountList = data.data
          this.totalCount = data.recordCount
        } else {
          this.interBranchAccountList = []
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getAccounts(selectedValue) {
      try {
        const response = await this.getAccountCategoryByCode(selectedValue)
        const { data, status } = response
        if (status === 200 && data.data) {
          this.accountNumberOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name,
            value: item.code.toString(),
          }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async onDeleteInterBranchTransaction(item) {
      const deleteInterBranchTransaction = async () => {
        try {
          this.showSpinner = true
          const response = await this.deleteInterBranchTransaction({ routeName: 'InterBranchTransactionAccount', id: item.id })
          const { status } = response
          if (status === 200) {
            this.fetchInterBranchAccounts()
            this.alertSuccess({ text: 'Deleted successfully' })
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(deleteInterBranchTransaction, { text: 'Are you sure you want to delete this Inter-Branch Transaction Account?' })
    },
    resetModal() {
      this.interBranchAccountFormData = { ...null }
      this.isLoading = false
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchInterBranchAccounts()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
